import { useState, useEffect, useCallback, useRef } from 'react';

const PREFERRED_VOICE_NAMES = [
  'Ava',
  'Samantha',
  'Susan',
  'Allison',
  'Zoe',
  'Victoria',
  'Google US English',
  'Microsoft Jenny',
  'Microsoft Aria',
];

function normalizeSpeechText(text: string): string {
  return text
    .replace(/\{\{[^}]+\}\}/g, ' ')
    .replace(/\[GAP:[^\]]+\]/g, ' ')
    .replace(/\bCPT\b/g, 'C P T')
    .replace(/\bICD-10\b/g, 'I C D ten')
    .replace(/\bDOB\b/g, 'date of birth')
    .replace(/\bMRN\b/g, 'M R N')
    .replace(/\bTKA\b/g, 'T K A')
    .replace(/\bMUA\b/g, 'M U A')
    .replace(/\bROM\b/g, 'range of motion')
    .replace(/\bPT\b/g, 'physical therapy')
    .replace(/\bHbA1c\b/gi, 'hemoglobin A one C')
    .replace(/([A-Z]{2,5})-(RT|LT)\b/g, '$1 $2')
    .replace(/(\d{1,3})\s*°/g, '$1 degrees')
    .replace(/[•*_#`]/g, ' ')
    .replace(/\s*[-–—]\s*/g, ', ')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .trim();
}

function splitIntoSpeakableChunks(text: string, maxLength = 220): string[] {
  const normalized = normalizeSpeechText(text);
  if (!normalized) return [];

  const sentences = normalized.match(/[^.!?]+[.!?]?/g) || [normalized];
  const chunks: string[] = [];
  let current = '';

  for (const sentence of sentences) {
    const next = sentence.trim();
    if (!next) continue;

    if (`${current} ${next}`.trim().length <= maxLength) {
      current = `${current} ${next}`.trim();
      continue;
    }

    if (current) chunks.push(current);

    if (next.length <= maxLength) {
      current = next;
      continue;
    }

    const words = next.split(/\s+/);
    current = '';
    for (const word of words) {
      if (`${current} ${word}`.trim().length > maxLength && current) {
        chunks.push(current);
        current = word;
      } else {
        current = `${current} ${word}`.trim();
      }
    }
  }

  if (current) chunks.push(current);
  return chunks;
}

function getBestVoice(): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  const englishVoices = voices.filter(voice => /^en[-_]/i.test(voice.lang));
  if (englishVoices.length === 0) return null;

  for (const preferredName of PREFERRED_VOICE_NAMES) {
    const match = englishVoices.find(voice =>
      voice.name.toLowerCase().includes(preferredName.toLowerCase()),
    );
    if (match) return match;
  }

  return englishVoices.find(voice => voice.localService) || englishVoices[0] || null;
}

export function useSpeechSynthesis() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const sessionRef = useRef(0);

  useEffect(() => {
    window.speechSynthesis?.addEventListener?.('voiceschanged', () => {});
    return () => {
      sessionRef.current += 1;
      window.speechSynthesis?.cancel();
    };
  }, []);

  const handleSpeak = useCallback((text: string) => {
    if (!window.speechSynthesis) return;

    if (isSpeaking) {
      sessionRef.current += 1;
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const chunks = splitIntoSpeakableChunks(text);
    if (chunks.length === 0) return;

    const sessionId = sessionRef.current + 1;
    sessionRef.current = sessionId;
    const voice = getBestVoice();
    let index = 0;

    const speakNext = () => {
      if (sessionRef.current !== sessionId) return;
      const chunk = chunks[index];
      if (!chunk) {
        setIsSpeaking(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(chunk);
      if (voice) utterance.voice = voice;
      utterance.rate = 0.88;
      utterance.pitch = 1.04;
      utterance.volume = 1;
      utterance.onend = () => {
        index += 1;
        window.setTimeout(speakNext, 120);
      };
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    };

    window.speechSynthesis.cancel();
    setIsSpeaking(true);
    window.setTimeout(speakNext, 50);
  }, [isSpeaking]);

  return { isSpeaking, handleSpeak };
}
